// The Shop block's config, read the same way by both templates and
// the editor. No imports, so the harness can run it in node.
//
// The shop sells through the organization's OWN checkout, one of two
// ways. A product can carry a link to a Stripe Payment Link, a PayPal
// button, a Square checkout or any other https page that takes the
// money. Or, with no link, when the organization has connected its
// Stripe account, a product with a price goes into a cart (shopCart.js)
// with its size picked on our page, the whole cart is paid for in one
// checkout on that account, and the order comes back to Website →
// Payments. Either way we see no card and
// hold no money. Same shape as the donation block.

export const SHOP_DEFAULTS = {
    columns: 3,
    button_label: 'Buy',
    note: '',
};

export const SHOP_ITEM_DEFAULTS = [
    { name: 'Trail crew t-shirt', price: '25', text: 'Soft cotton tee with our logo on the front.', options: 'S, M, L, XL', image: '', alt: '', url: '', sold_out: false },
    { name: 'Trucker hat', price: '22', text: 'Mesh back, one size fits most.', options: '', image: '', alt: '', url: '', sold_out: false },
];

// Where a checkout link goes, by its host, so the editor can confirm
// the link and the button can say where the visitor is about to land.
const PROVIDERS = [
    [/(^|\.)stripe\.com$/, 'Stripe'],
    [/(^|\.)paypal\.(com|me)$/, 'PayPal'],
    [/(^|\.)square\.(link|site)$|(^|\.)squareup\.com$/, 'Square'],
    [/(^|\.)myshopify\.com$|(^|\.)shop\.app$/, 'Shopify'],
    [/(^|\.)venmo\.com$/, 'Venmo'],
    [/(^|\.)buymeacoffee\.com$/, 'Buy Me a Coffee'],
    [/(^|\.)ko-fi\.com$/, 'Ko-fi'],
    [/(^|\.)givebutter\.com$/, 'Givebutter'],
    [/(^|\.)zeffy\.com$/, 'Zeffy'],
    [/(^|\.)bonfire\.com$/, 'Bonfire'],
    [/(^|\.)etsy\.com$/, 'Etsy'],
];

// Only an https page can take a payment. Anything else (a typo, a
// javascript: url, a plain http page) is no link at all, so the
// product shows without a Buy button rather than with a broken one.
export function checkoutHref(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';
    let u;
    try { u = new URL(s); } catch (e) { return ''; }
    return u.protocol === 'https:' && u.hostname.includes('.') ? u.href : '';
}

export function checkoutProvider(raw) {
    const href = checkoutHref(raw);
    if (!href) return '';
    const host = new URL(href).hostname.toLowerCase();
    const hit = PROVIDERS.find(([re]) => re.test(host));
    return hit ? hit[1] : '';
}

// A price a card checkout can charge: a bare number above zero. The
// server applies the same test to the saved block before it opens a
// checkout (WebsiteCheckout::findProduct).
export function cardPrice(raw) {
    const s = String(raw ?? '').trim();
    return /^\d+(\.\d{1,2})?$/.test(s) && Number(s) > 0;
}

// A bare number is a price in the site's currency; anything else
// ("$25", "From $18", "Free with membership") is shown as typed.
export function priceText(raw, currency = 'USD') {
    const s = String(raw ?? '').trim();
    if (!s) return '';
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return s;
    const n = Number(s);
    const whole = Number.isInteger(n);
    try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(n);
    } catch (e) {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: 2 }).format(n);
    }
}

// "S, M, L" or "S / M / L" → ['S', 'M', 'L'].
export function optionList(raw) {
    return String(raw || '')
        .split(/[,/|\n]/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 12);
}

// A row with no name is a half-added product in the editor; drawn, it
// is an empty card.
export function shopItems(config) {
    const rows = Array.isArray(config?.items) ? config.items : [];
    return rows
        .filter(Boolean)
        .map((r) => ({
            name: String(r.name || '').trim(),
            price: String(r.price ?? '').trim(),
            text: String(r.text || '').trim(),
            options: optionList(r.options),
            image: String(r.image || '').trim(),
            alt: String(r.alt || '').trim(),
            href: checkoutHref(r.url),
            // Anything typed in the link box, valid or not. A product
            // with a link is never offered a card checkout, which is
            // the server's rule too.
            has_link: String(r.url || '').trim() !== '',
            provider: checkoutProvider(r.url),
            sold_out: Boolean(r.sold_out),
        }))
        .filter((r) => r.name !== '')
        .slice(0, 24);
}

export function shopColumns(config) {
    const n = Number(config?.columns);
    return [2, 3, 4].includes(n) ? n : 3;
}

export function shopButtonLabel(config) {
    return String(config?.button_label || '').trim() || SHOP_DEFAULTS.button_label;
}
